// MACHINERY V2 (Stroje) — profile page body, layout C (M2.4).
//
// Split out of MachineProfileV2.tsx for the 300-line cap. MachineProfileV2 owns
// the ROUTE concerns (fetch, id guard, telemetry, derivations, tab resolution)
// and hands a loaded machine down; this file is the render for that machine —
// and therefore the only branch that mounts actions, dialogs and toasts. A
// component that only exists once a machine is loaded cannot dereference an
// undefined one, which is why the split falls here and not elsewhere.
//
// STRUCTURE (artboard 0504, manager ruling 2026-08-22 — designer layout wins):
//     breadcrumb -> [decommission banner] -> HERO -> pill tabs -> tab body
// No left identity column (folded into the hero), no KPI strip (§9 D3 cut it).
//
// The readiness card sits at the TOP OF THE TAB BODY AREA: the layout-C hi-fi
// is silent on readiness placement — see the header of MachineProfileV2.tsx for
// the full reasoning and the gate.
//
// ============================================================
//  >>> THE SERVICE-REPORT QUERY IS OWNED HERE, ONCE, FOR THE WHOLE PROFILE
//  >>> (M2.16d review, finding F-A). <<<
// ============================================================
// TWO tab bodies need it — Prehľad merges the reports into its activity list and
// Servisné správy renders them — and each used to mount its OWN `useSrTabQuery`
// instance. That was one hoistable defect and one latent one:
//
//  1. AN ORPHANED WRITE. `useSrTabQuery` dispatches on mount and does not abort
//     on cleanup, while `tabs/useMachineScope.ts` records ownership of the
//     app-wide `customerServiceReports` slot in PER-INSTANCE state. An instance
//     that unmounts mid-flight therefore still writes the shared slot, and the
//     instance that is alive cannot see that write — so a live instance holding
//     `owner === B` renders its skeleton over a slot that now holds A's list.
//     The hazard predates M2.16d, but M2.16d WIDENED it: Prehľad became the
//     default tab, so the fetch now fires on every profile open rather than only
//     when someone visits the SR tab. ONE owner that lives exactly as long as
//     the profile leaves no transient instance to orphan.
//  2. THE SR HALF OF PREHĽAD HAD NO FAILURE PATH (finding F3), because the
//     instance it mounted discarded `failed` and `refetch`. One shared instance
//     hands both to both consumers.
//
// IT IS DELIBERATELY NOT FIXED IN THE SLICE. `useMachineScope`'s header refuses
// a reset reducer on purpose: `customerServiceReports` is shared with the LIVE
// v1 pages, and new reducer surface would reach them.
//
// THE IDENTITY GUARANTEE IS UNCHANGED. `owns` is still per-request attribution,
// so `reports` is `undefined` — skeleton, never another machine's rows — until
// the slot provably holds THIS machine's response. And this component is itself
// remounted across a machine change (MachineProfileV2.tsx renders it only while
// `machine.id === id`), so the hoisted state cannot outlive its machine either.
//
// WHAT DOES CHANGE, STATED SO IT IS NOT DISCOVERED IN TESTING: the SR search,
// status filter and sort now survive a TAB switch within one profile, where they
// used to reset because the tab unmounted. They still reset on a MACHINE change,
// which is the doctrine that actually matters ("nothing survives a machine
// change" — useSrTabQuery.ts), and one fetch per profile replaces one per visit.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, useV2MediaQuery } from "../useV2MediaQuery";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import MachineryToastHost from "../feedback/MachineryToastHost";
import {
  MachineryV2CardPresentation,
  MachineryV2Telemetry,
} from "../fleet/cardStates";
import ReadinessPopupV2 from "../readiness/ReadinessPopupV2";
import ReadinessProfileCardV2 from "../readiness/ReadinessProfileCardV2";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import ProfileDecommissionBanner from "./ProfileDecommissionBanner";
import ProfileDialogs from "./ProfileDialogs";
import ProfileHero from "./ProfileHero";
import ProfileHeroPhone from "./ProfileHeroPhone";
import ProfileTabBody from "./ProfileTabBodies";
import ProfileTabs from "./ProfileTabs";
import { MachineryV2ProfileTab } from "./profileTabContract";
import { useProfileActions } from "./useProfileActions";
import { useSrTabQuery } from "./tabs/useSrTabQuery";

export interface ProfileBodyProps {
  machine: Machine;
  telemetry: MachineryV2Telemetry;
  presentation: MachineryV2CardPresentation;
  tabs: MachineryV2ProfileTab[];
  activeTab: MachineryV2ProfileTab;
  onTabChange: (t: MachineryV2ProfileTab) => void;
  readiness: MachineryV2ReadinessPresentation | null;
  readinessOpen: boolean;
  onReadinessOpenChange: (open: boolean) => void;
  onNavigateFleet: () => void;
}

const ProfileBody: React.FC<ProfileBodyProps> = ({
  machine,
  telemetry,
  presentation,
  tabs,
  activeTab,
  onTabChange,
  readiness,
  readinessOpen,
  onReadinessOpenChange,
  onNavigateFleet,
}) => {
  const { translate } = useTranslation("myMachinery");
  // ONE breakpoint read for the WHOLE profile, passed down. Two consumers need
  // it (the hero swap and the tab bar treatment) and the hook opens one
  // matchMedia listener per instance, so it is read here rather than in each.
  const belowDesktop = useV2MediaQuery(V2_BELOW_DESKTOP);
  // THE profile's one service-report query — see the header block. Both the
  // Prehľad tab and the Servisné správy tab render from this instance; neither
  // opens its own.
  const srQuery = useSrTabQuery(machine.id);
  const {
    actions,
    onCreateServiceReport,
    onCreateServiceRequest,
    onRestore,
    onDelete,
    deleteBusy,
    dialogProps,
  } = useProfileActions(machine);

  // ONE derivation, fed to BOTH the readiness card and its popup (M2.4
  // review). They disagreed: the card fell back through the CATALOG hierarchy
  // (`catalogModel.manufacturer.name`) while the popup read only the top-level
  // `machine.manufacturer.name`, so a sub-brand machine (Tajmac-ZPS ›
  // Manurhin) named one manufacturer on the card and a different one in the
  // popup's "not offered by X" line. Single derivation is exactly what
  // readinessPresentation exists to guarantee, one level up.
  const manufacturerName =
    machine.catalogModel?.manufacturer?.name || machine.manufacturer?.name;

  return (
    <div
      // >>> THIS DIV CARRIED `mv2-profile-touch` UNTIL M2.16e, AND CARRIES NO
      // CLASS NOW. <<< That class was the SCOPE of this page's below-1280 touch
      // additions, never a style, and both rules it scoped have moved out to
      // mobile/machinery-v2-touch.scss: `.btn` first, then
      // `button[aria-expanded]`, because SpecSectionsV2 also renders on
      // CatalogDetailMobile, outside this page. The tab search was never in the
      // scope at all — `.mv2-tabsearch` is floored BY CLASS on purpose, so that
      // it reaches PartsAddDialogV2's portalled content, which no page scope
      // could — so with the last scoped rule gone the class matched nothing and
      // stayed only as a hook nobody read. This page's controls are floored by
      // the wider rules; `aria-pressed` is still deliberately absent from all
      // of them (M2.16d review F6) — see that sheet's headers.
      style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0 }}
    >
      {/* Breadcrumb — artboard 0504: "Strojový park › <machine>". */}
      <div
        style={{
          padding: "10px 24px",
          borderBottom: "1px solid var(--border-soft)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12.5,
          color: "var(--muted)",
          minWidth: 0,
        }}
      >
        <button
          type="button"
          onClick={onNavigateFleet}
          style={{
            padding: 0,
            border: 0,
            background: "transparent",
            cursor: "pointer",
            font: "inherit",
            color: "var(--mod-accent)",
          }}
        >
          {translate("myMachinery:breadcrumbEquipment")}
        </button>
        <span style={{ color: "var(--faint)" }}>›</span>
        <span
          style={{
            color: "var(--ink-2)",
            fontWeight: 500,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {getMachineDisplayName(machine)}
        </span>
      </div>

      <main
        style={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowY: "auto",
          padding: "16px 24px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {machine.isDecommissioned === true && (
          <ProfileDecommissionBanner
            machine={machine}
            onRestore={onRestore}
            onDelete={onDelete}
            deleteBusy={deleteBusy}
          />
        )}

        {/* Two heroes, one breakpoint. Below 1280 the compact band replaces the
            desktop section outright rather than restyling it — the reasoning,
            and the three places it deliberately departs from boards 20/21, are
            in ProfileHeroPhone.tsx. The phone band takes NO telemetry: board 20
            moves the live ribbon's content into the Prehľad tab's status card
            directly below, and printing the state twice 40px apart is what the
            split avoids. */}
        {belowDesktop ? (
          <ProfileHeroPhone
            machine={machine}
            actions={actions}
            onCreateServiceReport={onCreateServiceReport}
            onCreateServiceRequest={onCreateServiceRequest}
          />
        ) : (
          <ProfileHero
            machine={machine}
            telemetry={telemetry}
            presentation={presentation}
            actions={actions}
            onCreateServiceReport={onCreateServiceReport}
            onCreateServiceRequest={onCreateServiceRequest}
          />
        )}

        <ProfileTabs
          tabs={tabs}
          active={activeTab}
          onChange={onTabChange}
          belowDesktop={belowDesktop}
        />

        {/* Top of the tab body area — placement note in MachineProfileV2.tsx. */}
        {readiness && (
          <ReadinessProfileCardV2
            presentation={readiness}
            oemPartnerName={machine.catalogModel?.oemPartnerName}
            manufacturerName={manufacturerName}
            onOpenDetails={() => onReadinessOpenChange(true)}
          />
        )}

        <div
          role="tabpanel"
          id="machinery-v2-tabpanel"
          aria-labelledby={`machinery-v2-tab-${activeTab}`}
          style={{ minWidth: 0 }}
        >
          <ProfileTabBody
            tab={activeTab}
            machine={machine}
            telemetry={telemetry}
            presentation={presentation}
            srQuery={srQuery}
          />
        </div>
      </main>

      {/* Mounted at PAGE level, outside every clickable surface — the fleet's
          rule, for the same reason. ReadinessPopupV2 also carries the standing
          stopPropagation guard on BOTH Dialog.Overlay and Dialog.Content. */}
      {readiness && readinessOpen && (
        <ReadinessPopupV2
          open
          onOpenChange={onReadinessOpenChange}
          presentation={readiness}
          machineOrModelName={getMachineDisplayName(machine)}
          oemPartnerName={machine.catalogModel?.oemPartnerName}
          manufacturerName={manufacturerName}
        />
      )}

      {/* The sanctioned v2 feedback primitive. */}
      <MachineryToastHost />

      <ProfileDialogs machine={machine} {...dialogProps} />
    </div>
  );
};

export default ProfileBody;
